import { useMutation, useQuery, useReactiveVar } from "@apollo/client";
import { useEffect, useState } from "react";

import {
  CREATE_CONNECT_DASHBOARD_LINK,
  CREATE_CONNECT_ONBOARDING_LINK,
  CREATE_PRO_BILLING_PORTAL_SESSION,
  GET_ORGANIZATION_BILLING_DETAILS,
} from "../../api/gqlQueries";
import {
  EConnectExpressOnboardingStatus,
  IOrganizationBillingDetailsData,
  IOrganizationParams,
} from "../../interfaces/interfaces";
import { organizationIdNameVar, smallHeaderTitle } from "../apollo/LocalState";
import { ReactComponent as AccountBalance } from "../../img/payments/account-balance.svg";
import Breadcrumbs from "../breadcrumbs/Breadcrumbs";
import { ReactComponent as CreditCard } from "../../img/payments/credit-card.svg";
import { ReactComponent as ExternalLink } from "../../img/ExternalLink.svg";
import { ReactComponent as MoneyCashBill } from "../../img/payments/money-cash-bill.svg";
import { ReactComponent as ProAndTenant } from "../../img/payments/pro-and-tenant.svg";

const PaymentsAndPayouts: React.FC = () => {
  const { organizationId, organizationName } = useReactiveVar(
    organizationIdNameVar
  );

  const [errorAction, setErrorAction] = useState<"payments" | "payouts" | null>(
    null
  );

  const crumbs = [
    {
      name: organizationName,
      path: `/${organizationId}/organization`,
    },
    { name: "Payments & Payouts", path: "" },
  ];

  const [createProBillingPortalSession, { loading }] = useMutation(
    CREATE_PRO_BILLING_PORTAL_SESSION
  );

  const [createConnectDashboardLink, { loading: payoutsLoading }] = useMutation(
    CREATE_CONNECT_DASHBOARD_LINK
  );

  const [createConnectOnboardingLink, { loading: onboardingLoading }] =
    useMutation(CREATE_CONNECT_ONBOARDING_LINK);

  const { data: billingData } = useQuery<
    IOrganizationBillingDetailsData,
    IOrganizationParams
  >(GET_ORGANIZATION_BILLING_DETAILS, {
    skip: !organizationId,
    variables: { organizationId },
  });

  const isPayoutsOnboardingComplete =
    billingData?.organization.connectExpressOnboardingStatus ===
      EConnectExpressOnboardingStatus.OnboardingComplete ||
    billingData?.organization.connectExpressOnboardingStatus ===
      EConnectExpressOnboardingStatus.DetailsSubmitted;

  useEffect(() => {
    smallHeaderTitle("Payments & Payouts");
    return () => {
      smallHeaderTitle("");
    };
  }, []);

  const handleManagePayments = async () => {
    // Open the tab synchronously inside the click handler so it isn't blocked
    // by the popup blocker, then point it at the URL once the mutation returns.
    setErrorAction(null);
    const newTab = window.open("", "_blank");
    try {
      const { data } = await createProBillingPortalSession({
        variables: { orgId: organizationId },
      });
      const url = data?.createProBillingPortalSession?.portalUrl;
      if (newTab && url) {
        newTab.location.href = url;
      } else {
        newTab?.close();
        setErrorAction("payments");
      }
    } catch {
      newTab?.close();
      setErrorAction("payments");
    }
  };

  const handleManagePayouts = async () => {
    setErrorAction(null);
    const newTab = window.open("", "_blank");
    try {
      const { data } = await createConnectDashboardLink({
        variables: { orgId: organizationId },
      });
      const url = data?.createConnectDashboardLink?.dashboardUrl;
      if (newTab && url) {
        newTab.location.href = url;
      } else {
        newTab?.close();
        setErrorAction("payouts");
      }
    } catch {
      newTab?.close();
      setErrorAction("payouts");
    }
  };

  const handleSetUpPayouts = async () => {
    setErrorAction(null);
    const newTab = window.open("", "_blank");
    try {
      const { data } = await createConnectOnboardingLink({
        variables: { orgId: organizationId },
      });
      const url = data?.createConnectOnboardingLink?.onboardingUrl;
      if (newTab && url) {
        newTab.location.href = url;
      } else {
        newTab?.close();
        setErrorAction("payouts");
      }
    } catch {
      newTab?.close();
      setErrorAction("payouts");
    }
  };

  return (
    <div className="page-container center-align">
      <div className="page-container-inner padding-bottom-large">
        <Breadcrumbs
          crumbs={crumbs}
          rootCrumb={{ name: "Organizations", path: "/" }}
        />
        <div className="header unit-title payments-and-payouts-title">
          <MoneyCashBill
            className="payments-and-payouts-header-icon"
            title="Payments & Payouts"
          />
          Payments &amp; Payouts
        </div>
        <div className="margin-top-small" />
        <div className="payments-and-payouts-list">
          <div className="payments-and-payouts-section">
            <p className="menu">Payments</p>
            <div className="payments-and-payouts-section-content">
              <p>
                Add, edit, or remove your payment methods linked to ProView. The
                saved payment information will be used when you enroll customers
                in Brilliant Max.
              </p>
              <button
                className="payments-and-payouts-link"
                disabled={loading}
                onClick={handleManagePayments}
                type="button"
              >
                <span>Manage Payment Method</span>
                <ExternalLink />
              </button>
              {errorAction === "payments" && (
                <p className="payments-and-payouts-error">
                  Something went wrong. Please try again.
                </p>
              )}
            </div>
          </div>
          <div className="payments-and-payouts-section">
            <p className="menu">Payouts</p>
            <div className="payments-and-payouts-section-content">
              {isPayoutsOnboardingComplete ? (
                <>
                  <p>
                    Add, edit, or remove your bank accounts linked to ProView.
                  </p>
                  <button
                    className="payments-and-payouts-link"
                    disabled={payoutsLoading}
                    onClick={handleManagePayouts}
                    type="button"
                  >
                    <span>Manage Payouts</span>
                    <ExternalLink />
                  </button>
                </>
              ) : (
                <>
                  <p>
                    Add, edit, or remove your bank accounts linked to ProView
                    with Stripe. You will need to set up Stripe Connect Express
                    to access payouts.
                  </p>
                  <button
                    className="payments-and-payouts-link"
                    disabled={onboardingLoading}
                    onClick={handleSetUpPayouts}
                    type="button"
                  >
                    <span>Set Up Payouts</span>
                    <ExternalLink />
                  </button>
                </>
              )}
              {errorAction === "payouts" && (
                <p className="payments-and-payouts-error">
                  Something went wrong. Please try again.
                </p>
              )}
            </div>
          </div>
          <div className="payments-and-payouts-card">
            <p className="menu">How it works</p>
            <div className="payments-and-payouts-card-row">
              <ProAndTenant
                className="payments-and-payouts-card-icon"
                title="Pro and Tenant"
              />
              <div className="payments-and-payouts-card-content">
                <p className="placeholder">
                  Pro-Managed and Self-Managed Subscriptions
                </p>
                <p>
                  Customers may subscribe to Brilliant Max either through a pro
                  (provides a commission) or by self-upgrading through the
                  mobile app (does not offer a commission).
                </p>
              </div>
            </div>
            <div className="payments-and-payouts-card-row">
              <AccountBalance
                className="payments-and-payouts-card-icon"
                title="Account Balance"
              />
              <div className="payments-and-payouts-card-content">
                <p className="placeholder">Payment</p>
                <p>
                  Save your payment method to subscribe your customers to
                  Brilliant Max. As a pro, you may sell pro-managed annual
                  subscriptions at $69.99/year.
                </p>
              </div>
            </div>
            <div className="payments-and-payouts-card-row">
              <CreditCard
                className="payments-and-payouts-card-icon"
                title="Credit Card"
              />
              <div className="payments-and-payouts-card-content">
                <p className="placeholder">Payouts</p>
                <p>
                  Pros receive a commission of 20% when customers are subscribed
                  to Brilliant Max. In order to receive your payout, you need to
                  have a bank account linked to your ProView account.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentsAndPayouts;
